/**
 * SubscriptionContext (iter52)
 *
 * Provee la suscripción del tenant activo + helper hasFeature(name).
 * Se monta dentro del AppShell autenticado y se refresca al focus de la pestaña.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { dteApi } from "../lib/api";
import { useAuth } from "./AuthContext";
import { IS_LOCAL_EDITION } from "../lib/edition";

const Ctx = createContext({
  sub: null,
  loading: true,
  refresh: () => {},
  hasFeature: () => false,
  upgradeFor: null,
  setUpgradeFor: () => {},
});

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeFor, setUpgradeFor] = useState(null); // { feature, planLabel, message, upgradeOptions }

  const refresh = useCallback(async () => {
    if (IS_LOCAL_EDITION) {
      setSub({
        plan: "local",
        planLabel: "Local",
        status: "active",
        dte_limit: null,
        dte_used_this_month: 0,
        features: [
          "emit_dte", "sales_book_basic", "sales_book_full", "purchase_book_manual",
          "export_basic", "pdf_reader", "auto_purchase_classification", "iva_auto",
          "f14", "anexos", "accounting_basic", "dashboard", "export_full",
          "accounting_full", "period_close", "audit_logs", "backup", "bulk_exports",
          "multi_company", "roles", "accountant_panel", "inventory",
        ],
      });
      setLoading(false);
      return;
    }
    if (!user) {
      setSub(null);
      setLoading(false);
      return;
    }
    try {
      const s = await dteApi.subscription();
      setSub(s);
    } catch {
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const hasFeature = useCallback(
    (name) => Array.isArray(sub?.features) && sub.features.includes(name),
    [sub]
  );

  return (
    <Ctx.Provider value={{ sub, loading, refresh, hasFeature, upgradeFor, setUpgradeFor }}>
      {children}
    </Ctx.Provider>
  );
}

export const useSubscription = () => useContext(Ctx);
